/**
 * iter1035 — BHI Reading Level context
 *
 * Global state for the Explain-Like-I'm-5 toggle (feature #3).
 * Two levels:
 *   • "clinical" — current medical terminology (default)
 *   • "plain"    — friendly Bulgarian phrasing
 *
 * Persisted to localStorage under `bhi_reading_level`.
 * Consumers use `useReadingLevel()` and `<PlainText clinical="…" plain="…" />`.
 */
import React, { createContext, useContext, useState, useCallback } from "react";

const ReadingLevelCtx = createContext({
  level: "clinical",
  setLevel: () => {},
  isPlain: false,
});

export function BhiReadingLevelProvider({ children }) {
  const [level, setLevelState] = useState(() => {
    try {
      const stored = localStorage.getItem("bhi_reading_level");
      return stored === "plain" ? "plain" : "clinical";
    } catch {
      return "clinical";
    }
  });

  const setLevel = useCallback((next) => {
    setLevelState(next);
    try { localStorage.setItem("bhi_reading_level", next); } catch { /* private mode */ }
  }, []);

  return (
    <ReadingLevelCtx.Provider value={{ level, setLevel, isPlain: level === "plain" }}>
      {children}
    </ReadingLevelCtx.Provider>
  );
}

export function useReadingLevel() {
  return useContext(ReadingLevelCtx);
}

/**
 * <PlainText clinical="Take 2000 IU Vit D3 with K2 in the morning"
 *            plain="Sunshine capsule in the morning, with fatty food" />
 * Renders one string based on active reading level. Falls back to clinical
 * if plain is missing.
 */
export function PlainText({ clinical, plain, as: Tag = "span", className = "", ...rest }) {
  const { isPlain } = useReadingLevel();
  const text = isPlain && plain ? plain : clinical;
  return <Tag className={className} {...rest}>{text}</Tag>;
}
